/* System defined core library */
import { Component, HostListener, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { MetricData, MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../shared/models/scheduling/slot.model';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';

/* User defined enums/constants */
import { ComponentType, MetricType } from '../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-find-availability-testcenter',
  templateUrl: './find-availability-testcenter.component.html'
})
export class FindAvailabilityTestcenterComponent implements OnInit {

  @HostListener('window:popstate', ['$event'])

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }

  /* User defined private/public variables  */
  public isBackToBackRequired: boolean = false;
  public selectedAppointments: AppointmentDetail[] = [];
  public selectedIndex: number = 0;
  public isNextButtonEnable: boolean;
  public IsCurrentFormChanged: boolean;
  public IsHoldInProgress: boolean;
  public holdSlotObj: Hold;
  public slotsCheck: boolean;
  public metricDetails: MetricDetails;
  public prevSelectedSlot: Slot;
  public prevSelectedHoldObj: any = [];
  public prevSelectedHoldObjArr: any = [];
  public prevSlotSelections: any = [];
  public prevSelectionsArr: SiteSlot[] = [];
  public prevSites: any = [];
  public region: any;
  public terminationUrl: string = "";
  public terminationText: string = 'reconfirmationComponent.termUrl';
  public pathLock: boolean = false;


  /* Service subscriptions */
  selectedAppointments$: Subscription;
  hold$: Subscription;
  region$: Subscription;
  translateLanguage$: Subscription;

  prevSelectedSlot$: Subscription;
  prevSelectedHoldObj$: Subscription;
  prevSelectionsArr$: Subscription; // selected sites
  prevSlotSelections$: Subscription; // selected slots
  prevSites$: Subscription; // previous searched sites

  constructor(
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    this.isBackToBackRequired = this.selectedExamDetailService.isBackToBackRequired;

    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    this.hold$ = selectedExamDetailService.hold.subscribe(hold => {
      this.holdSlotObj = hold;
    });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    // selected sites until now
    this.prevSelectionsArr$ = this.selectedExamDetailService.prevSelections.subscribe(siteSlots => {
      this.prevSelectionsArr = siteSlots || [];

    });

    this.region$ = selectedExamDetailService.region.subscribe(region => {
      this.region = region;
    });

    // selected slots until now
    this.prevSlotSelections$ = this.selectedExamDetailService.prevSlotSelections.subscribe(slots => {
      this.prevSlotSelections = slots || [];
    });

    this.prevSites$ = this.selectedExamDetailService.prevSites.subscribe(sites => {
      this.prevSites = sites || [];
    });

    this.prevSelectedSlot$ = this.selectedExamDetailService.slot.subscribe(slot => {
      this.prevSelectedSlot = slot;
    });

    this.isNextButtonEnable = true;
    this.IsHoldInProgress = false;
    this.IsCurrentFormChanged = false;

    this.prevSelectedHoldObj$ = selectedExamDetailService.prevSelectedHoldObj.subscribe(
      prevSelectedHoldObj => {
        this.prevSelectedHoldObj = prevSelectedHoldObj;
        if (this.prevSelectedHoldObj) {
          this.prevSelectedHoldObjArr = prevSelectedHoldObj;
        }
      }
    );

    this.pathLock = this.selectedExamDetailService.setSeatAvailabilityPathLock;
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);

    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      }
    );
  }

  ngOnInit() {
    let clientCode = this.selectedExamDetailService.clientCode ? this.selectedExamDetailService.clientCode.toLowerCase() : '';
    if (clientCode === 'cfa' || clientCode === 'cfad' || clientCode === 'cfas') {
      this.terminationText = 'CFA_Cancel_Term';
    }

    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.FindAvailabilityTestcenterComponent)) return;
    this.terminationUrl = this.selectedExamDetailService.terminationUrl;

    this.slotsCheck = true;
  }

  goToPrevPage() {
    this.clearDataOnGoingBack();
    this.routingService.backwardNavigationSearch(ComponentType.FindAvailabilityTestcenterComponent);
  }

  clearDataOnGoingBack() {
    this.selectedExamDetailService.setSiteSlot(null);
    this.selectedExamDetailService.setSlot(null);
    this.selectedExamDetailService.setSiteSlots(null);
    this.selectedExamDetailService.setSelectedAvailableSlots(null);
    if (!this.isBackToBackRequired && this.selectedAppointments.length > 1) {
      this.selectedAppointments[this.selectedIndex].appointmentSlot = new AppointmentSlot();
      this.selectedAppointments[this.selectedIndex].hold = new Hold();
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
      if (this.prevSelectionsArr.length || this.prevSites.length) {
        this.prevSelectionsArr[this.selectedIndex] = null;
        this.prevSites[this.selectedIndex] = [];
      }
      if (this.prevSlotSelections.length) {
        this.prevSlotSelections[this.selectedIndex] = [];
      }
      this.selectedExamDetailService.setPrevSites(this.prevSites);
      this.selectedExamDetailService.setPrevSlotSelections(this.prevSlotSelections);
      this.selectedExamDetailService.setPrevSelections(this.prevSelectionsArr);
    }
    else {
      this.prevSites = [];
      this.prevSlotSelections = [];
      this.prevSelectionsArr = null;
      this.selectedExamDetailService.setPrevSites(this.prevSites);
      this.selectedExamDetailService.setPrevSlotSelections(this.prevSlotSelections);
      this.selectedExamDetailService.setPrevSelections(this.prevSelectionsArr);
      this.selectedAppointments.forEach((appointment) => {
        appointment.appointmentSlot = new AppointmentSlot();
        appointment.hold = new Hold();
      });
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    }
  }

  goToHome() {
    this.routingService.homeNavigation();
  }

  /* Go to client page after appointment is scheduled. */
  goToTerminationUrl(): void {
    this.routingService.navigateToTerm();
  }

  public isDisable(): boolean {
    return !this.slotsCheck;
  }

  public setSelectedSiteDetails(selectedSiteDetails: any): void {
    this.selectedAppointments = selectedSiteDetails.selectedAppointments;
    this.selectedIndex = selectedSiteDetails.selectedIndex;
    this.holdSlotObj = selectedSiteDetails.holdSlotObj;
    this.slotsCheck = selectedSiteDetails.slotsCheck;
  }

  public setApptDetailData() {
    this.selectedAppointments[this.selectedIndex].hold.id = this.holdSlotObj.slot.id.toString();

    this.selectedExamDetailService.setHold(this.holdSlotObj);
    this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);


    this.selectedAppointments[this.selectedIndex].appointmentSlot.id = this.holdSlotObj.slot.id;
    this.selectedAppointments[this.selectedIndex].appointmentSlot.href = this.holdSlotObj.slot.uri;

    this.selectedAppointments[this.selectedIndex].appointmentSlot.startDateTime = new Date(this.utilityService.getFormatToFullMMDDYYYY(this.holdSlotObj.slot.startDateTime, "/"));

    this.selectedAppointments[this.selectedIndex].appointmentSlot.duration = this.holdSlotObj.slot.duration;
    this.selectedAppointments[this.selectedIndex].appointmentSlot.testEndTime = this.utilityService.getStartEndFormat(
      UtilityService.addMins(
        this.selectedAppointments[this.selectedIndex].appointmentSlot.startDateTime,
        this.selectedAppointments[this.selectedIndex].appointmentSlot.duration
      )
    );
    this.selectedAppointments[this.selectedIndex].hold.slot = this.holdSlotObj.slot;
    this.selectedExamDetailService.SetSlotDataSource(this.selectedAppointments[this.selectedIndex].appointmentSlot);
    this.selectedExamDetailService.isRescheduleSlotSelected = true;
    // this.selectedExamDetailService.rescheduleFeeAmount = 0;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

  }

  /* Set metric details in shared service */
  private setMetricDetails(metricType: MetricType, siteId?: string, slotdate?: any): void {
    if (this.metricDetails == null || this.metricDetails == undefined) {
      this.metricDetails = new MetricDetails();
    }
    if (metricType === MetricType.BestSite && this.region) {
      this.metricDetails.bestSite =
        new MetricData(MetricType.BestSite,
          { "latitude": this.region.lat, "longitude": this.region.long, "siteId": siteId },
          slotdate
        );
      this.selectedExamDetailService.setMetricDetails(this.metricDetails);
    }
    else if (metricType === MetricType.Selected && this.region) {
      this.metricDetails.selected =
        new MetricData(MetricType.Selected,
          { "latitude": this.region.lat, "longitude": this.region.long, "siteId": siteId },
          slotdate
        );
      this.selectedExamDetailService.setMetricDetails(this.metricDetails);
    }
    else if (metricType === MetricType.BestDate) {
      this.metricDetails.bestDate = new MetricData(
        MetricType.BestDate,
        {
          "searchedSiteId": siteId,
          "offeredSiteId": siteId,
          "searchedDate": this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedAppointments[0].hold.slot.startDateTime), "-")
        },
        this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedAppointments[0].hold.slot.startDateTime), "-")
      );
      this.selectedExamDetailService.setMetricDetails(this.metricDetails);
    }
  }
}
